// Toolkit panel summoned by the dominant-hand A button. Layout:
//
//   Header band:  a SETTINGS gear in the top-right corner — utility chrome,
//     deliberately set apart from the modes/tools so it's not read as
//     either. Opens the sticky settings panel (passthrough / recenter /
//     help / dominant hand).
//
//   Below the gear, stacked sections separated by divider lines so the
//   tiers read distinctly (each section is a uniform 2-col grid; odd count
//   leaves the trailing slot blank):
//     • tools   — Draw / Select / Resize Canvas. Mutually exclusive; the
//                 active one lights the white pill.
//     • toggles — Grid | Edges. Independent on/off, which is why they sit
//                 apart from the pick-one tools above.
//
// paint() is state-driven; pickButton() decodes a UV hit to a button id.
// Both share toolkitLayout() (+ gearRect for settings) so hit-testing
// always matches what's drawn.

import * as THREE from "three";
import { type TopMode } from "../interaction/tool";
import { paintGearIcon } from "./icons";
import {
  TEXT_DIM,
  glassFont,
  paintGlassButton,
  paintGlassPanel,
  setupPanelTexture,
} from "./glass";

const CANVAS_W = 1024;
const CANVAS_H = 1024;
const PANEL_W = 0.32;
const PANEL_H = 0.32;
const MARGIN = 56;
const GAP = 28; // gap between rows within a section
const SECTION_GAP = 32; // gap between sections (divider line sits in it)
const HEAD_H = 40; // space for a section heading above its buttons
// Settings gear: a circular button in the top-right header band.
const GEAR_SIZE = 96;
// The button grid sits below the gear band.
const GRID_TOP = MARGIN + GEAR_SIZE + GAP;
// Fixed button-row height. The toolkit stacks three sections — Tool (2 rows)
// / Display (1 row) / Canvas (1 row) = 4 rows — each with a heading, plus one
// intra-section gap (the 2-row Tool section) and two section gaps.
const ROW_H =
  (CANVAS_H - GRID_TOP - MARGIN - 3 * HEAD_H - 1 * GAP - 2 * SECTION_GAP) / 4;

export const TOOLKIT_BUTTON_IDS = [
  "settings",
  "draw",
  "paint",
  "bucket",
  "select",
  "move",
  "resize",
  "grid",
  "edges",
  "fill",
  "clear",
] as const;
export type ToolkitButtonId = (typeof TOOLKIT_BUTTON_IDS)[number];

const canvas = document.createElement("canvas");
canvas.width = CANVAS_W;
canvas.height = CANVAS_H;
const texture = new THREE.CanvasTexture(canvas);
setupPanelTexture(texture);

const material = new THREE.MeshBasicMaterial({
  map: texture,
  transparent: true,
  side: THREE.DoubleSide,
  depthTest: false,
  depthWrite: false,
});

export const toolkitPanelMesh = new THREE.Mesh(
  new THREE.PlaneGeometry(PANEL_W, PANEL_H),
  material,
);
toolkitPanelMesh.renderOrder = 950;
toolkitPanelMesh.visible = false;

interface ButtonRect {
  x: number;
  y: number;
  w: number;
  h: number;
}
interface PlacedButton {
  id: ToolkitButtonId;
  rect: ButtonRect;
}

const SUB_COLS = 2;
function colWidth(cols: number): number {
  return (CANVAS_W - 2 * MARGIN - (cols - 1) * GAP) / cols;
}

// Settings gear — circular button in the top-right header band.
function gearRect(): ButtonRect {
  return {
    x: CANVAS_W - MARGIN - GEAR_SIZE,
    y: MARGIN,
    w: GEAR_SIZE,
    h: GEAR_SIZE,
  };
}

// The toolkit stacks vertically as a series of sections, each with a
// subtle heading and (between sections) a divider line so the tiers read
// distinctly:
//   • Tool (Draw / Select / Resize) — pick one
//   • Display (Grid | Edges) — independent on/off
//   • Canvas (Fill | Clear) — one-shot actions on the whole work volume
// Buttons within a section flow in a uniform 2-col grid (blank trailing
// slot for odd counts).
interface Section {
  heading: string;
  ids: ToolkitButtonId[];
}
function sections(): Section[] {
  return [
    { heading: "Tool", ids: ["draw", "paint", "select", "resize"] },
    { heading: "Display", ids: ["grid", "edges"] },
    { heading: "Canvas", ids: ["fill", "clear"] },
  ];
}

interface PlacedHeading {
  text: string;
  x: number;
  y: number; // top of the heading text
}
interface ToolkitLayout {
  buttons: PlacedButton[];
  headings: PlacedHeading[];
  // Y coordinates (canvas px) of the divider lines between sections.
  dividers: number[];
}

// Full layout — sections stacked from GRID_TOP, each led by a heading and
// (after the first) a divider in the section gap. Shared by paint + pick.
// (Settings is the gear, handled via gearRect.)
function toolkitLayout(): ToolkitLayout {
  const w = colWidth(SUB_COLS);
  const buttons: PlacedButton[] = [];
  const headings: PlacedHeading[] = [];
  const dividers: number[] = [];

  const secs = sections();
  let y = GRID_TOP;
  for (let si = 0; si < secs.length; si++) {
    const { heading, ids } = secs[si];
    if (si > 0) {
      dividers.push(y + SECTION_GAP / 2);
      y += SECTION_GAP;
    }
    headings.push({ text: heading, x: MARGIN + 6, y });
    y += HEAD_H;
    const rows = Math.ceil(ids.length / SUB_COLS);
    for (let j = 0; j < ids.length; j++) {
      const row = Math.floor(j / SUB_COLS);
      const col = j % SUB_COLS;
      buttons.push({
        id: ids[j],
        rect: { x: MARGIN + col * (w + GAP), y: y + row * (ROW_H + GAP), w, h: ROW_H },
      });
    }
    y += rows * ROW_H + (rows - 1) * GAP;
  }
  return { buttons, headings, dividers };
}

function buttonLabel(id: ToolkitButtonId): string {
  switch (id) {
    case "settings":
      return "Settings";
    case "draw":
      return "Draw";
    case "paint":
      return "Paint";
    case "bucket":
      return "Paint Bucket";
    case "select":
      return "Select";
    case "move":
      return "Move";
    case "resize":
      return "Resize Canvas";
    case "grid":
      return "Grid";
    case "edges":
      return "Edges";
    case "fill":
      return "Fill";
    case "clear":
      return "Clear";
  }
}

export interface ToolkitPanelState {
  // Active top-level mode — drives the active subtool highlight
  // (Draw / Select / Resize).
  topMode: TopMode;
  gridOn: boolean;
  edgesOn: boolean;
  hot: ToolkitButtonId | null;
}

// Subtool (white) highlight — the active sub-tool / view toggle.
function isSubtoolActive(
  id: ToolkitButtonId,
  state: ToolkitPanelState,
): boolean {
  switch (id) {
    case "draw":
      return state.topMode === "sketch";
    case "paint":
      return state.topMode === "paint";
    case "bucket":
      return state.topMode === "bucket";
    case "select":
      return state.topMode === "select";
    case "resize":
      return state.topMode === "resize";
    case "grid":
      return state.gridOn;
    case "edges":
      return state.edgesOn;
    default:
      // settings is momentary.
      return false;
  }
}

export function paintToolkitPanel(state: ToolkitPanelState): void {
  const ctx = canvas.getContext("2d")!;
  paintGlassPanel(ctx, CANVAS_W, CANVAS_H);

  const { buttons, headings, dividers } = toolkitLayout();

  // Section dividers — hairlines spanning the interior width.
  ctx.lineWidth = 3;
  ctx.strokeStyle = "rgba(255, 255, 255, 0.16)";
  for (const dy of dividers) {
    ctx.beginPath();
    ctx.moveTo(MARGIN, dy);
    ctx.lineTo(CANVAS_W - MARGIN, dy);
    ctx.stroke();
  }

  // Section headings — small, dim, uppercase labels above each tier.
  ctx.font = glassFont("600 28px");
  ctx.fillStyle = TEXT_DIM;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  for (const hd of headings) {
    ctx.fillText(hd.text.toUpperCase(), hd.x, hd.y);
  }

  for (const { id, rect } of buttons) {
    paintGlassButton(ctx, rect.x, rect.y, rect.w, rect.h, {
      label: buttonLabel(id),
      active: isSubtoolActive(id, state),
      hot: id === state.hot,
      fontPx: 42,
    });
  }

  // Settings gear — circular glass button + cog glyph, top-right.
  const g = gearRect();
  paintGlassButton(ctx, g.x, g.y, g.w, g.h, {
    hot: state.hot === "settings",
    radius: GEAR_SIZE / 2,
  });
  paintGearIcon(ctx, g.x + g.w / 2, g.y + g.h / 2, GEAR_SIZE * 0.62);

  texture.needsUpdate = true;
}

function inRect(x: number, y: number, r: ButtonRect): boolean {
  return x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;
}

export function pickToolkitButton(
  uvX: number,
  uvY: number,
): ToolkitButtonId | null {
  const x = uvX * CANVAS_W;
  const y = (1 - uvY) * CANVAS_H;
  if (inRect(x, y, gearRect())) return "settings";
  for (const { id, rect } of toolkitLayout().buttons) {
    if (inRect(x, y, rect)) return id;
  }
  return null;
}
